import {
  ColumnDef,
  functionalUpdate,
  InitialTableState,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { useSearchDataContextWithCatalogFallback } from 'src/contexts/SearchDataContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import {
  useUserHasMultiValueTagsInColumnsFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { NoData } from 'src/tables/Table';
import { Table } from 'src/tables/TableV2/Table';
import { createNumericColorGradientThresholds } from 'src/utils/cellUtils';
import {
  defaultSalesFlattenedColumnsConfig,
  filterColumnsByFeatures,
  getSaleColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { SALE_MAIN_COLUMNS_V2 } from 'src/utils/columns/sales/salesColumnUtils.constants';
import {
  evaluateFormula,
  filterCustomColumnsForSale,
} from 'src/utils/columns/sales/salesCustomColumnUtils';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { createAggregatedSale } from 'src/utils/saleUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasAllPermissions } from 'src/utils/userPermissionUtils';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Permission,
  Sale,
  UserSetting,
} from 'src/WebApiController';

import {
  SalesTableColumnId,
  SalesTableFlattenedColumnId,
} from '../../utils/columns/sales/salesColumnUtils.types';
import { useSeatingColumnSorting } from '../common/hooks/useSeatingColumnSorting';
import { createCustomColumn } from '../TableV2/factories/customColumnFactory';
import { createTagColumn } from '../TableV2/factories/tagColumnFactory';
import { getSaleTableColumnConfigsV2 } from './configs/SaleTableColumnsConfigV2';
import { SALE_TABLE_FLATTENED_COLUMNS_CONFIG_v2 } from './configs/SaleTableFlattenedColumnsConfigV2';
import { SaleWithEvent } from './SalesTable.type';

export type SalesTableFlattenedV2Props = {
  sales?: Sale[];
  salesSummary?: Sale | null;
  salesCount: number;
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  disablePagination?: boolean;
  useVirtuoso?: boolean;
  columnVisibilityOverride?: (
    | SalesTableColumnId
    | SalesTableFlattenedColumnId
  )[];
  formatOptionOverrides?: FormatOptionEntries;
  disableSelection?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
  ignoreMaxCount?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  serverSidePageIndex?: number;
  manualPagination?: boolean;
  rowCount?: number;
  pageSize?: number;
  enableColumnFilters?: boolean;
  isSearchV2?: boolean;
  totalCount?: number | null;
  footerCta?: React.ReactNode;
};

const PAGE_SIZE = 100;

export const SalesTableFlattenedV2 = ({
  sales,
  salesSummary,
  salesCount,
  failedToRetrieveData,
  onMount,
  onUnmount,
  initState,
  disablePagination,
  useVirtuoso,
  columnVisibilityOverride,
  formatOptionOverrides,
  disableSelection,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  ignoreMaxCount,
  onPaginationChange: onPaginationChangeCallback,
  onSortingChange: onSortingChangeCallback,
  serverSidePageIndex,
  manualPagination,
  rowCount,
  pageSize,
  enableColumnFilters,
  isSearchV2,
  totalCount,
  footerCta,
}: SalesTableFlattenedV2Props) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Sale
  );
  const hasMultiValueTagsInColumns = useUserHasMultiValueTagsInColumnsFeature();

  const showMainColumnsOnly = false;
  const { data: catalog, isItemsLoading } =
    useSearchDataContextWithCatalogFallback();

  const seatingColumnSorting = useSeatingColumnSorting<SaleWithEvent>(
    SalesTableColumnId.Seating,
    SectionType.SalesFlattened
  );

  const { data, hasNoDataLoad } = useMemo(() => {
    const nonNullSales = sales
      ?.map((sale) => {
        const { event, performer, venue } = getEventPerformerVenue(
          sale.viagVirtId,
          catalog
        );

        if (event) {
          return {
            event: event.event,
            sale,
            performer,
            venue,
            isFlattenedMode: true,
          } as SaleWithEvent;
        }

        return null;
      })
      ?.filter((s) => s != null)
      ?.map((s) => s!);

    const data =
      nonNullSales == null || isItemsLoading
        ? (generateShimmeringRows(salesCount) as null[])
        : nonNullSales;

    const hasNoDataLoad =
      !ignoreMaxCount && salesCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;

    return { data, hasNoDataLoad };
  }, [sales, isItemsLoading, salesCount, ignoreMaxCount, catalog]);

  const hasViewProceeds = useUserHasAnyOfPermissions(
    Permission.Sales_ViewProceeds,
    Permission.Sales_ViewRecentProceeds
  );

  const { appContext, loginContext } = useAppContext();
  const {
    setGroupItems,
    getGroupToggleState,
    getSelection,
    selectionMode,
    updateGroupFilteredIds,
  } = useMultiSelectionContext();

  const saleSelection = getSelection(NO_GROUP_ID);

  const { sorting, setSorting } = useActiveSortTableColumnContext();
  const [pagination, setPagination] = useState<PaginationState | undefined>(
    disablePagination
      ? undefined
      : {
          pageIndex:
            serverSidePageIndex ?? (initState?.pagination?.pageIndex || 0),
          pageSize: pageSize ?? PAGE_SIZE,
        }
  );

  useEffect(() => {
    if (pagination && pageSize != null && pagination.pageSize !== pageSize) {
      setPagination((prev) =>
        prev
          ? {
              ...prev,
              pageSize: pageSize,
              pageIndex: 0,
            }
          : prev
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const rowSelection = useMemo(() => {
    return data
      ?.flatMap((d) => [d?.sale])
      ?.reduce<Record<string, boolean>>((result, sale) => {
        if (sale?.id) {
          const isGroupSelectedWithoutClientSideFilter =
            getGroupToggleState(NO_GROUP_ID).isGroupSelected &&
            !saleSelection?.items.itemIds.length;

          result[sale.id] =
            (isGroupSelectedWithoutClientSideFilter ||
              (saleSelection?.items.itemIds ?? [])?.includes(
                String(sale?.id)
              )) ??
            false;
        }
        return result;
      }, {});
  }, [data, getGroupToggleState, saleSelection?.items.itemIds]);

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      if (disableSelection) return;
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setGroupItems(NO_GROUP_ID, newSelections);
    },
    [disableSelection, rowSelection, setGroupItems]
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ sorting });
  }, [sorting, onUnmount]);

  const {
    value:
      storedSaleFlattenedColumnOrderSetting = defaultSalesFlattenedColumnsConfig,
  } = useServerUserSetting<SalesTableColumnId[]>({
    id: UserSetting.SaleFlattenedColumnOrder,
  });
  const { value: storedSaleColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: UserSetting.SaleFlattenedColumnsEnabled,
  });

  const { value: customSaleColumns = [] } = useServerUserSetting<
    CustomSalesColumn[]
  >({
    id: UserSetting.SaleCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Sale,
    true
  );

  const customSaleColumnsFiltered = filterCustomColumnsForSale(
    customSaleColumns,
    tagsMetadataNumeric
  );

  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      saleSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      saleSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  const displayedColumnsConfig = useMemo(() => {
    if (showMainColumnsOnly) {
      return SALE_MAIN_COLUMNS_V2.filter((c) =>
        c === SalesTableColumnId.Checkbox ? showCheckbox : true
      ).reduce<ColumnDef<SaleWithEvent | null>[]>((acc, columnId) => {
        const columnConfig = Object.values(
          getSaleTableColumnConfigsV2(isSearchV2)
        ).find((column) => column.id === columnId);
        if (columnConfig) {
          acc.push(columnConfig);
        }
        return acc;
      }, []);
    }

    const saleColumnOrder = columnVisibilityOverride
      ? columnVisibilityOverride
      : storedSaleFlattenedColumnOrderSetting.filter(
          // If storedSaleColumnsEnabledSetting has never been set, default to true
          (c) => storedSaleColumnsEnabledSetting?.includes(c) ?? true
        );

    // TODO: Add SALE_TABLE_FLATTENED_COLUMNS_CONFIG_V2
    const saleTableFlattenedColumnsConfig = Object.values(
      SALE_TABLE_FLATTENED_COLUMNS_CONFIG_v2
    );
    const saleTableColumnsConfig = Object.values(
      getSaleTableColumnConfigsV2(isSearchV2)
    );

    const columns = new Set(
      columnVisibilityOverride
        ? columnVisibilityOverride
            .map(
              (cid) =>
                saleTableColumnsConfig.find((c) => c.id === cid) ??
                saleTableFlattenedColumnsConfig.find((c) => c.id === cid)!
            )

            .map((item) => item.id!)
        : [
            // Add unconfigurable columns first
            ...saleTableFlattenedColumnsConfig
              .filter((column) => {
                const columnDef = getSaleColumnConfigById(
                  column.id as SalesTableFlattenedColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );
                const hasPermissionForColumn =
                  columnDef.requiredPermissions.length === 0 ||
                  hasAllPermissions(
                    loginContext?.user,
                    columnDef.requiredPermissions
                  );

                return (
                  !columnDef.isConfigurable &&
                  hasFeatureForColumn &&
                  hasPermissionForColumn
                );
                // return true;
                // return !columnDef.isConfigurable;
              })
              .map((item) => item.id as SalesTableFlattenedColumnId),
            ...saleTableColumnsConfig
              .filter((column) => {
                const columnDef = getSaleColumnConfigById(
                  column.id as SalesTableColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );
                const hasPermissionForColumn =
                  columnDef.requiredPermissions.length === 0 ||
                  hasAllPermissions(
                    loginContext?.user,
                    columnDef.requiredPermissions
                  );

                const isConfigurable = columnDef.isConfigurable;

                return (
                  !isConfigurable &&
                  hasFeatureForColumn &&
                  hasPermissionForColumn
                );
              })
              .map((item) => item.id as SalesTableColumnId),
            // Add user defined columns next
            ...filterColumnsByFeatures(
              saleColumnOrder,
              SectionType.SalesFlattened,
              customSaleColumnsFiltered,
              loginContext?.user,
              appContext?.features,
              tagsMetadata ?? []
            ),
          ]
    );

    if (!columnVisibilityOverride) {
      if (showCheckbox) {
        if (!alwaysShowCheckbox) {
          columns.delete(SalesTableColumnId.Actions);
        }
      } else {
        columns.delete(SalesTableColumnId.Checkbox);
      }
    }

    return Array.from(columns).reduce<ColumnDef<SaleWithEvent | null>[]>(
      (acc, columnId) => {
        if (tagsMetadata?.find((t) => t.key === columnId)) {
          acc.push(
            createTagColumn<SaleWithEvent | null>({
              columnId,
              tagsAccessor: (data) => data?.sale?.tags,
              currencyAccessor: (data) => data?.sale?.currency,
              sectionType: SectionType.SalesFlattened,
              hasMultiValueTagsInColumns,
              ...(isSearchV2 && { enableSorting: false }),
            })
          );
        } else if (customSaleColumnsFiltered.find((c) => c.id === columnId)) {
          acc.push(
            createCustomColumn<SaleWithEvent | null>({
              columnId,
              formula: customSaleColumnsFiltered.find((c) => c.id === columnId)
                ?.formula,
              formulaAccessor: (data, formula) =>
                formula && data?.sale
                  ? (evaluateFormula(formula, data.sale) ?? null)
                  : null,
              currencyAccessor: (data) => data?.sale?.currency,
              customColumnsAccessor: () => customSaleColumnsFiltered,
              ...(isSearchV2 && { enableSorting: false }),
            })
          );
        } else {
          const columnConfig1 = saleTableColumnsConfig.find(
            (column) => column.id === columnId
          );
          if (columnConfig1) acc.push(columnConfig1);

          const columnConfig2 = saleTableFlattenedColumnsConfig.find(
            (column) => column.id === columnId
          );
          if (columnConfig2) acc.push(columnConfig2);
        }

        return acc;
      },
      []
    );
  }, [
    showMainColumnsOnly,
    columnVisibilityOverride,
    storedSaleFlattenedColumnOrderSetting,
    customSaleColumnsFiltered,
    hasMultiValueTagsInColumns,
    loginContext?.user,
    appContext?.features,
    tagsMetadata,
    showCheckbox,
    storedSaleColumnsEnabledSetting,
    alwaysShowCheckbox,
    isSearchV2,
  ]);

  const [visibleRowIds, setVisibleRowIds] = useState<number[]>();

  const salesSummaryFiltered = useMemo(() => {
    if (!salesSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
      return createAggregatedSale(
        data
          ?.filter((d) => d && visibleRowIds.includes(d.sale.id))
          .map((d) => d!.sale)
      );
    }
    return salesSummary;
  }, [visibleRowIds, data, salesSummary, hasTableTotalsFeature]);

  // Calculate thresholds for P&L and Margin columns
  const numericColorThresholds = useMemo(() => {
    const pandlValues = data
      .map((d) => d?.sale?.pandL?.amt)
      .filter((val): val is number => val != null);

    const marginValues = data
      .map((d) => d?.sale?.margin)
      .filter((val): val is number => val != null);

    return {
      [SalesTableColumnId.PandL]:
        createNumericColorGradientThresholds(pandlValues),
      [SalesTableColumnId.Margin]:
        createNumericColorGradientThresholds(marginValues),
    };
  }, [data]);

  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      if (!pagination) return;

      const newPagination = functionalUpdate(updater, pagination);
      setPagination(newPagination);
      onPaginationChangeCallback?.(newPagination);
    },
    [pagination, onPaginationChangeCallback]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = functionalUpdate(updater, sorting);
      setSorting(newSorting);
      onSortingChangeCallback?.(newSorting);
    },
    [setSorting, sorting, onSortingChangeCallback]
  );

  // Memoize state object to prevent VirtuosoTable rerenders
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      columnVisibility: {
        checkbox: showCheckbox,
      },
    }),
    [pagination, sorting, rowSelection, showCheckbox]
  );

  // Memoize meta object to prevent VirtuosoTable rerenders
  const tableMeta = useMemo(
    () => ({
      sectionType: SectionType.SalesFlattened,
      showSelectAllCheckbox: showSelectAllOnHeader,
      summaryData:
        data?.[0] && salesSummaryFiltered
          ? {
              event: data[0].event,
              sale: salesSummaryFiltered,
            }
          : undefined,
      numericColorThresholds,
    }),
    [showSelectAllOnHeader, data, salesSummaryFiltered, numericColorThresholds]
  );

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : hasNoDataLoad ? (
    <NoData>
      <WarningMessage
        message={
          <FormatContent
            id={FormatContentId.ApplyFilterToReduceCountsToSeeData}
            params={[MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS.toString()]}
          />
        }
      />
    </NoData>
  ) : data?.length > 0 ? (
    <ColumnResizingContextProvider userSettingId={UserSetting.SaleColumnWidths}>
      <Table<SaleWithEvent>
        useVirtuoso={useVirtuoso}
        actionColId={SalesTableColumnId.Actions}
        data={data as SaleWithEvent[]}
        columns={
          displayedColumnsConfig.filter((c) =>
            c.id === 'proceeds' ? hasViewProceeds : true
          ) as ColumnDef<SaleWithEvent>[]
        }
        pagination={
          disablePagination
            ? undefined
            : {
                pageSize: pagination?.pageSize ?? PAGE_SIZE,
                variant: 'input',
                totalCount,
              }
        }
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        sortingFns={{ ...seatingColumnSorting }}
        onRowSelectionChange={onRowSelectionChange}
        alwaysShowCheckbox={alwaysShowCheckbox}
        manualPagination={manualPagination}
        rowCount={rowCount}
        enableColumnFilters={enableColumnFilters}
        getGroupId={() => NO_GROUP_ID}
        getItemId={(data) => String(data?.sale?.id)}
        state={tableState}
        meta={tableMeta}
        footerCta={footerCta}
      />
    </ColumnResizingContextProvider>
  ) : null;
};
